import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageBackground from './PageBackgroundBeam';
import PageFooter from './PageFooter';
import './Top25Table.css';

const YEARS = [
  { year: 2020, path: '/top25-categorias-2020' },
  { year: 2021, path: '/top25-categorias-2021' },
  { year: 2022, path: '/top25-categorias-2022' },
  { year: 2023, path: '/top25-categorias-2023' },
  { year: 2024, path: '/top25-categorias-2024' },
  { year: 2025, path: '/top25-categorias' },
];

function formatNumber(n) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatPct(n) {
  return n.toFixed(2) + '%';
}

function Sparkline({ inv6y }) {
  const chronological = [...inv6y].reverse();
  const inv2025 = inv6y[0];
  const inv2020 = inv6y[5];
  const isUp = inv2025 >= inv2020;
  const color = isUp ? '#4ade80' : '#f87171';

  const w = 120;
  const h = 40;
  const padX = 4;
  const padY = 6;

  const max = Math.max(...chronological);
  const min = Math.min(...chronological);
  const range = max - min || 1;

  const points = chronological.map((val, i) => {
    const x = padX + (i / (chronological.length - 1)) * (w - padX * 2);
    const y = padY + (1 - (val - min) / range) * (h - padY * 2);
    return `${x},${y}`;
  });

  const polyline = points.join(' ');
  const firstX = padX;
  const lastX = padX + (w - padX * 2);
  const areaPoints = `${firstX},${h - padY} ${polyline} ${lastX},${h - padY}`;

  return (
    <div className="sparkline-container">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="sparkline-svg">
        <defs>
          <linearGradient id={`grad-cat-${isUp ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#grad-cat-${isUp ? 'up' : 'down'})`} />
        <polyline points={polyline} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {(() => {
          const lastPoint = points[points.length - 1].split(',');
          return <circle cx={lastPoint[0]} cy={lastPoint[1]} r="2.5" fill={color} />;
        })()}
      </svg>
      <span className={`sparkline-value ${isUp ? 'spark-up' : 'spark-down'}`}>
        {inv2020 > 0 ? (isUp ? '+' : '') + (((inv2025 - inv2020) / inv2020) * 100).toFixed(0) + '%' : 'NEW'}
      </span>
    </div>
  );
}

function Top25CatTable({ year, totalMarket, totalSpots, data }) {
  const navigate = useNavigate();
  const location = useLocation();

  const maxInv = data[0].inv;

  let cumPct = 0;
  const rows = data.map((row) => {
    const pct = (row.inv / totalMarket) * 100;
    cumPct += pct;
    return { ...row, pct, cumPct };
  });

  const topInvSum   = data.reduce((s, r) => s + r.inv, 0);
  const topSpotsSum = data.reduce((s, r) => s + r.spots, 0);
  const topPct      = (topInvSum / totalMarket) * 100;
  const hasSparkline = data[0] && data[0].inv6y;

  return (
    <PageBackground>
      <div className="top20-page">
        <header className="top20-header">
          <button className="btn-back" onClick={() => navigate('/top25-anunciantes')}>
            &#8592; Anunciantes
          </button>
          <div className="top20-title-block">
            <h1 className="top20-title">Top 25 Categor&iacute;as</h1>
            <p className="top20-subtitle">Honduras &middot; {year} &middot; Inversi&oacute;n en USD</p>
          </div>
          <img src={require('../assets/logo-publisearch.png')} alt="Publisearch" className="header-logo-inline" />
        </header>

        <nav className="year-nav">
          {YEARS.map((y) => (
            <button
              key={y.year}
              className={`year-btn ${location.pathname === y.path ? 'year-btn-active' : ''}`}
              onClick={() => navigate(y.path)}
            >
              {y.year}
            </button>
          ))}
          <span className="year-nav-sep"></span>
          <button
            className="year-btn year-btn-link"
            onClick={() => navigate('/ranking-historico-categorias')}
          >
            Ranking Hist&oacute;rico
          </button>
          <span className="year-nav-sep"></span>
          <button
            className="year-btn year-btn-link"
            onClick={() => navigate('/top25-anunciantes')}
          >
            Top 25 Anunciantes
          </button>
        </nav>

        <div className="top20-table-wrapper">
          <table className="top20-table">
            <thead>
              <tr>
                <th className="col-pos">#</th>
                <th className="col-name">Categor&iacute;a</th>
                <th className="col-inv">Valor Mercado</th>
                <th className="col-pct">%</th>
                <th className="col-cum">% Acum.</th>
                <th className="col-spots">Spots</th>
                <th className="col-sub">Top 3 Anunciantes</th>
                {hasSparkline && <th className="col-spark">Inv. 6Y</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.pos} className="table-row" style={{ animationDelay: `${idx * 0.04}s` }}>
                  <td className="col-pos">
                    <span className={`pos-badge ${row.pos <= 3 ? 'pos-top3' : ''}`}>
                      {row.pos}
                    </span>
                  </td>
                  <td className="col-name">
                    <span className="advertiser-name">{row.categoria}</span>
                    <div className="inv-bar-container">
                      <div
                        className="inv-bar"
                        style={{ width: `${(row.inv / maxInv) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="col-inv">${formatNumber(row.inv)}</td>
                  <td className="col-pct">{formatPct(row.pct)}</td>
                  <td className="col-cum">{formatPct(row.cumPct)}</td>
                  <td className="col-spots">{formatNumber(row.spots)}</td>
                  <td className="col-sub">
                    <div className="sub-tags">
                      {row.top3.map((s, i) => (
                        <span key={i} className="sub-tag">{s}</span>
                      ))}
                    </div>
                  </td>
                  {hasSparkline && (
                    <td className="col-spark">
                      <Sparkline inv6y={row.inv6y} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td className="col-pos"></td>
                <td className="col-name"><span className="total-label">TOTAL MERCADO</span></td>
                <td className="col-inv">${formatNumber(totalMarket)}</td>
                <td className="col-pct">100%</td>
                <td className="col-cum"></td>
                <td className="col-spots">{formatNumber(totalSpots)}</td>
                <td className="col-sub"></td>
                {hasSparkline && <td className="col-spark"></td>}
              </tr>
              <tr className="subtotal-row">
                <td className="col-pos"></td>
                <td className="col-name"><span className="subtotal-label">TOP 25</span></td>
                <td className="col-inv">${formatNumber(topInvSum)}</td>
                <td className="col-pct">{formatPct(topPct)}</td>
                <td className="col-cum"></td>
                <td className="col-spots">{formatNumber(topSpotsSum)}</td>
                <td className="col-sub"></td>
                {hasSparkline && <td className="col-spark"></td>}
              </tr>
            </tfoot>
          </table>
        </div>
        <PageFooter />
      </div>
    </PageBackground>
  );
}

export default Top25CatTable;
